/**
 * Screen 2 — Dashboard.
 *
 * Five numbers, each a link into the list that explains it. Nothing here is a
 * dead end: if the "Expired 12" card is wrong or surprising, one tap shows
 * exactly which twelve people it means.
 */
import { Link } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import { loadDashboardStats } from '../db/queries';
import { formatLongDate, todayISO } from '@shared/dates';

export default function Dashboard() {
  const today = todayISO();
  const stats = useLiveQuery(() => loadDashboardStats(today), [today], undefined);

  if (!stats) return <p className="py-20 text-center text-white/30">Loading…</p>;

  return (
    <div className="mx-auto max-w-5xl px-5 py-6">
      <div className="mb-6">
        <h1 className="text-4xl text-brand">Today</h1>
        <p className="text-lg text-white/50">{formatLongDate(today)}</p>
      </div>

      {/* Primary actions — the two things done most often. */}
      <div className="mb-8 grid gap-3 sm:grid-cols-2">
        <Link to="/new" className="btn text-lg">
          + New subscription
        </Link>
        <Link to="/walk-in" className="btn-ghost text-lg">
          ⚡ Walk-in
        </Link>
      </div>

      {/*
        Membership counts only. Revenue and outstanding totals are deliberately
        NOT shown here — takings are management information, and this screen is
        on an unattended monitor at the counter. Those figures live in the
        portal, behind a manager login. The "members owing" count stays because
        chasing a balance is a front desk job; the amount is not.
      */}
      {/* Two rows of two, not one row of four — four narrow columns squeeze the
          numbers and read worse on the desk monitor. */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card
          to="/members?status=active"
          label="Active members"
          value={stats.activeMembers}
          tone="active"
        />
        <Card
          to="/renewals"
          label="Expiring within 7 days"
          value={stats.expiringSoon}
          tone="expiring"
          hint="Go to renewals"
        />
        <Card
          to="/members?status=expired"
          label="Expired"
          value={stats.expired}
          tone="expired"
        />
        <Card
          to="/members?status=due"
          label="Members with a balance"
          value={stats.membersOwing}
          tone={stats.membersOwing > 0 ? 'due' : 'active'}
          hint={stats.membersOwing > 0 ? 'Ask them to settle up' : 'Everyone is settled'}
        />
      </div>

      <p className="mt-6 text-center text-sm text-white/25">
        {stats.totalMembers} {stats.totalMembers === 1 ? 'member' : 'members'} on record
      </p>
    </div>
  );
}

function Card({
  to,
  label,
  value,
  tone,
  hint,
}: {
  to: string;
  label: string;
  value: number;
  tone: 'active' | 'expiring' | 'expired' | 'due';
  hint?: string;
}) {
  const accent = {
    active: 'text-status-active',
    expiring: 'text-status-expiring',
    expired: 'text-status-expired',
    due: 'text-status-due',
  }[tone];

  return (
    <Link
      to={to}
      className="card block p-6 transition-all duration-brand hover:-translate-y-[3px] hover:border-brand"
    >
      <div className="label mb-2">{label}</div>
      <div className={`tabular text-4xl font-bold ${accent}`}>{value}</div>
      {hint && <div className="mt-2 text-xs text-white/35">{hint}</div>}
    </Link>
  );
}
